import {
  ChangeHandler,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormReturn,
} from 'react-hook-form';

/**
 * Extended register return type with additional properties for form field management.
 * Includes the current value, error message, and enhanced onChange/onBlur handlers.
 */
export type ExtendedFormRegisterReturn<TFieldValues extends FieldValues> = {
  name: string;
  ref: (instance: HTMLInputElement | null) => void;
  value: any;
  error?: string;
  onChange: ChangeHandler;
  onBlur: (e: React.FocusEvent<Element>) => void;
};

/**
 * Extended register function type that returns additional field metadata.
 */
export type ExtendedFormRegister<TFieldValues extends FieldValues> = <
  K extends Path<TFieldValues>,
>(
  name: K,
  options?: RegisterOptions<TFieldValues, K>,
) => ExtendedFormRegisterReturn<TFieldValues>;

/**
 * Configuration for creating a form register function with custom behavior.
 */
type CreateFormRegisterConfig<TFieldValues extends FieldValues> = {
  /**
   * React Hook Form instance
   */
  form: UseFormReturn<TFieldValues>;
  /**
   * Callback invoked when form is valid and should be submitted.
   * Called on blur after successful validation.
   */
  onSubmit: (value: TFieldValues) => void | Promise<void>;
  /**
   * Callback invoked when form validation fails.
   */
  onError?: () => void;
  /**
   * Whether to show validation errors.
   * If false, errors won't be displayed even if validation fails.
   */
  showErrors: boolean;
  /**
   * Mode determines when to trigger submission:
   * - 'auto-save': Submit on every blur if form is valid (edit mode)
   * - 'explicit': Never auto-submit, requires manual submission (create mode)
   */
  mode: 'auto-save' | 'explicit';
};

/**
 * Creates a type-safe register function with enhanced onChange/onBlur behavior.
 * This function abstracts the common pattern used across PDF, Slides, Video, and Link ViewModels.
 *
 * Features:
 * - Type-safe field access with autocomplete
 * - Auto-validation on change and blur
 * - Conditional auto-save based on mode
 * - Error message extraction
 * - Current field value tracking
 *
 * @example
 * ```typescript
 * const form = useForm<ContentPDF['value']>({ ... });
 * const [showErrors, setShowErrors] = useState(false);
 *
 * const register = createFormRegister({
 *   form,
 *   onSubmit: (value) => model.update(id, toUpdateContentInput({ type: 'ContentPDF', value })),
 *   onError: () => setShowErrors(true),
 *   showErrors,
 *   mode: 'auto-save',
 * });
 *
 * // Usage in component
 * <TextInput {...register('title')} />
 * ```
 */
export const createFormRegister = <TFieldValues extends FieldValues>(
  config: CreateFormRegisterConfig<TFieldValues>,
): ExtendedFormRegister<TFieldValues> => {
  const { form, onSubmit, onError, showErrors, mode } = config;

  return <K extends Path<TFieldValues>>(
    name: K,
    options?: RegisterOptions<TFieldValues, K>,
  ): ExtendedFormRegisterReturn<TFieldValues> => {
    const res = form.register(name, options);

    const onChange: ChangeHandler = (e) => {
      // Set field value with validation
      form.setValue(name, e.target.value, {
        shouldValidate: true,
        shouldTouch: true,
      });

      // Trigger validation for this field
      form.trigger(name);

      // Call original onChange handler
      return res.onChange(e);
    };

    const onBlur = (e: React.FocusEvent<Element>) => {
      // Trigger validation
      form.trigger(name);

      // Auto-save only in 'auto-save' mode
      if (mode === 'auto-save') {
        form.handleSubmit(onSubmit, onError)(e);
      }

      // Call original onBlur handler
      return res.onBlur(e);
    };

    return {
      ...res,
      value: form.watch(name),
      onChange,
      onBlur,
      error: showErrors ? form.getFieldState(name).error?.message : undefined,
    };
  };
};

/**
 * Creates an onChange handler for programmatic field updates (e.g., from custom components).
 * Unlike register().onChange which handles input events, this is for direct value updates.
 *
 * @example
 * ```typescript
 * const onChange = createOnChangeHandler({
 *   form,
 *   onSubmit: (value) => model.update(id, value),
 *   onError: () => setShowErrors(true),
 *   mode: 'auto-save',
 * });
 *
 * // Usage with custom component
 * <CustomSelect
 *   value={form.watch('status')}
 *   onChange={(newValue) => onChange('status', newValue)}
 * />
 * ```
 */
export const createOnChangeHandler = <TFieldValues extends FieldValues>(
  config: Omit<CreateFormRegisterConfig<TFieldValues>, 'showErrors'>,
) => {
  const { form, onSubmit, onError, mode } = config;

  return <K extends Path<TFieldValues>>(
    name: K,
    value: TFieldValues[K],
  ): void => {
    form.setValue(name, value, {
      shouldValidate: true,
      shouldTouch: true,
    });

    // Auto-save only in 'auto-save' mode
    if (mode === 'auto-save') {
      form.handleSubmit(onSubmit, onError)();
    }
  };
};

/**
 * Helper to determine the mode based on modal state.
 * Used to switch between 'auto-save' (edit mode) and 'explicit' (create mode).
 */
export const getFormMode = (
  modalMode: 'create' | 'edit' | 'none' | string,
): 'auto-save' | 'explicit' => {
  return modalMode === 'edit' ? 'auto-save' : 'explicit';
};
